import React from 'react';
import { useAuth } from '../../hooks/useAuth';
import { LoginForm } from './LoginForm';
import ErrorBoundary from '../common/ErrorBoundary';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

interface AuthGuardProps {
  children: React.ReactNode;
}

// Custom fallback for auth-specific errors
function AuthErrorFallback() {
  const handleReload = () => window.location.reload();
  const handleClearStorage = () => {
    // Clear potential corrupted auth data
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };
  
  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 text-orange-500 mb-4">
            <AlertTriangle className="w-full h-full" />
          </div>
          <CardTitle>Authentication Error</CardTitle>
          <CardDescription>
            We're having trouble verifying your authentication. This usually resolves itself by refreshing the page or clearing browser data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={handleReload} className="w-full" variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Page
          </Button>
          <Button onClick={handleClearStorage} className="w-full" variant="destructive">
            Clear Data & Refresh
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Show loading state while actually loading auth status
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Handle auth query errors gracefully
  if (user === undefined && !isLoading) {
    // This indicates a query error (not loading, but also no user data)
    return <AuthErrorFallback />;
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  // Wrap authenticated content in error boundary
  return (
    <ErrorBoundary fallback={<AuthErrorFallback />}>
      {children}
    </ErrorBoundary>
  );
}